import type { AuthenticatedUser } from '@projoint/shared';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Auth state — the access token + the current user.
 *
 * The **refresh token never lives in JS** anymore — it's an httpOnly cookie
 * (`projoint_rt`, set by the API) that script can't read, so an XSS can't
 * exfiltrate it.  The short-lived **access token lives in memory only** (not
 * persisted): on reload it's re-minted by a silent `POST /api/auth/refresh`
 * against the cookie (see `useAuthBootstrap`).  Nothing here is written to
 * `localStorage` except the non-secret `user` snapshot, kept for an instant
 * boot render; authoritative state still comes from `/me` on every reload.
 */
export interface AuthState {
  /** Local lifecycle fence. Rotation keeps it; clear/explicit sign-in replace it. */
  generation: number;
  accessToken: string | null;
  user: AuthenticatedUser | null;
  /** False until the one-time boot silent-refresh has resolved (success or not). */
  bootstrapped: boolean;
  /**
   * Whose session the last `clear()` ended (persisted, non-secret).  Other tabs
   * mirror a cleared snapshot only when it ended THEIR user — a tab of another
   * account sharing this browser must not log everyone out with it.
   */
  endedUserId: string | null;

  /** Set once after a successful signin / signup / signin/2fa response. */
  setSession: (session: { accessToken: string; user: AuthenticatedUser }) => void;
  /** Update the access token after a refresh — keep `user`. */
  setTokens: (tokens: { accessToken: string }) => void;
  /** Update user (after PATCH /me, or after fresh /me load). */
  setUser: (user: AuthenticatedUser) => void;
  /** Mark the boot silent-refresh as done so the auth-guard can act. */
  setBootstrapped: () => void;
  /**
   * Take over the refresh cookie's account in a tab that holds NO live access
   * token (boot / offline renewal) when it differs from the remembered user
   * snapshot: the cookie jar is authoritative, the snapshot is only a render
   * cache.  Drops the stale snapshot (its offline cache is purged by the user
   * change) and replaces the identity; `/me` then loads the real user.
   */
  adoptSession: (session: { accessToken: string; userId: string }) => void;
  /** Clear everything — logout / 401 dead-end. Boot stays resolved. */
  clear: () => void;
}

const STORAGE_KEY = 'projoint:auth';

/** The account this tab's session belongs to: the user snapshot, else the
 *  access token's `sub` (a tab between token mint and `/me`). */
function sessionUserId(state: Pick<AuthState, 'user' | 'accessToken'>): string | null {
  if (state.user) return state.user.id;
  try {
    const part = state.accessToken?.split('.')[1];
    if (!part) return null;
    const sub = (JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as { sub?: unknown }).sub;
    return typeof sub === 'string' ? sub : null;
  } catch {
    return null;
  }
}
let identityGeneration = 0;
let lastIdentityUserId: string | null = null;
const identityListeners = new Set<() => void>();
function replaceIdentity(userId: string): void {
  identityGeneration += 1;
  lastIdentityUserId = userId;
  for (const listener of identityListeners) listener();
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      generation: 0,
      accessToken: null,
      user: null,
      bootstrapped: false,
      endedUserId: null,
      setSession: ({ accessToken, user }) => {
        replaceIdentity(user.id);
        set((state) => ({ accessToken, user, generation: state.generation + 1 }));
      },
      setTokens: ({ accessToken }) => set({ accessToken }),
      setUser: (user) => set({ user }),
      setBootstrapped: () => set({ bootstrapped: true }),
      adoptSession: ({ accessToken, userId }) => {
        replaceIdentity(userId);
        // endedUserId stays null: this is a hand-over, not a sign-out, so the
        // persisted `user: null` must not end other tabs' sessions.
        set((state) => ({ accessToken, user: null, endedUserId: null, generation: state.generation + 1 }));
      },
      clear: () => {
        try {
          // Draft turns contain private text. Recovery codes are never stored here.
          for (const key of Object.keys(sessionStorage)) {
            if (key.startsWith('projoint:assistant-draft:')) sessionStorage.removeItem(key);
          }
          sessionStorage.removeItem('projoint:auth-return');
          // Unsent chat drafts (localStorage, `shared/lib/chat-drafts.ts`) are private text too.
          for (const key of Object.keys(localStorage)) {
            if (key.startsWith('pj-chat-draft:')) localStorage.removeItem(key);
          }
        } catch { /* Storage may be disabled; in-memory termination still applies. */ }
        set((state) => ({ accessToken: null, user: null, endedUserId: sessionUserId(state), generation: state.generation + 1 }));
      },
    }),
    {
      name: STORAGE_KEY,
      // Persist ONLY the non-secret user snapshot. Tokens stay out of storage.
      partialize: (s) => ({ user: s.user, endedUserId: s.endedUserId }),
    },
  ),
);

lastIdentityUserId = useAuthStore.getState().user?.id ?? null;
/** Distinguishes logout from any subsequent replacement login, even after it
 * also ends. It never persists credentials or signs in from a storage event. */
export const getAuthIdentityGeneration = (): number => identityGeneration;
export function subscribeAuthIdentity(listener: () => void): () => void {
  identityListeners.add(listener);
  return () => { identityListeners.delete(listener); };
}
export const captureAuthLifetime = () => ({ generation: useAuthStore.getState().generation, identity: identityGeneration });
export type AuthLifetime = ReturnType<typeof captureAuthLifetime>;
export const isAuthLifetimeCurrent = (lifetime: AuthLifetime): boolean =>
  lifetime.generation === useAuthStore.getState().generation && lifetime.identity === identityGeneration;

/** Read the current access token without re-rendering. Useful inside the api client. */
export const getAccessToken = (): string | null => useAuthStore.getState().accessToken;

/**
 * Cross-tab auth sync.
 *
 * Tokens are no longer shared via storage (the refresh cookie is shared by the
 * browser automatically; each tab mints its own access token from it).  This
 * listener only needs to propagate **logout**: when one tab clears the
 * persisted `user`, mirror the clear here so the other tabs drop their session
 * and the auth-guard bounces them to /login.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    try {
      const next = event.newValue
        ? (JSON.parse(event.newValue) as { state?: { user?: AuthenticatedUser | null; endedUserId?: string | null } }).state
        : null;
      const loggedOut = !next || !next.user;
      if (typeof next?.user?.id === 'string' && next.user.id !== lastIdentityUserId) replaceIdentity(next.user.id);
      // A snapshot that names whose session ended only ends THAT user's tabs
      // (another account's tab dying, or a boot hand-over, must not cascade).
      // Storage wiped / a pre-marker payload → mirror the logout as before.
      const mine = sessionUserId(useAuthStore.getState());
      const endedSomeoneElse = !!next && 'endedUserId' in next && next.endedUserId !== mine;
      if (loggedOut && !endedSomeoneElse) {
        useAuthStore.getState().clear();
      }
    } catch {
      // Bad payload → ignore; the other tab re-emits on its next mutation.
    }
  });
}
